var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { AnalyticsV1, LoaderComp } from 'ui-kit';
import { setSuiNodeUrl } from 'alphafi-sdk';
import { getAnalyticsDataV2, initializeAlphaFi } from 'sui-alpha-sdk';
const AnalyticsPage = ({ themeName }) => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [error, setError] = useState(null);
    const lastThemeName = useRef(themeName);
    const init = (force) => __awaiter(void 0, void 0, void 0, function* () {
        setSuiNodeUrl('https://fullnode.mainnet.sui.io:443');
        yield initializeAlphaFi({}, force);
    });
    // Helper function to format date strings to "DD MMM" format
    const formatDate = (dateString) => {
        // Handle timestamp strings (convert to number first)
        const timestamp = isNaN(Number(dateString)) ? dateString : Number(dateString);
        const date = new Date(timestamp);
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return dateString; // Return original string if parsing fails
        }
        return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;
    };
    const fetchAnalyticsData = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        try {
            yield init(false);
            const response = yield getAnalyticsDataV2();
            // Transform the data to match AnalyticsData interface
            const transformedData = Object.assign(Object.assign({}, response), { dailyRevenue: ((_a = response.dailyRevenue) === null || _a === void 0 ? void 0 : _a.total) || '0', historicalTVL: {
                    time: ((_b = response.historicalTVL) === null || _b === void 0 ? void 0 : _b.map((item) => formatDate(item.time))) || [],
                    label: 'Total Value Locked',
                    TVL: ((_c = response.historicalTVL) === null || _c === void 0 ? void 0 : _c.map((item) => parseFloat(item.TVL))) || [],
                }, alphaHistoricalMarketCap: {
                    time: ((_d = response.alphaHistoricalMarketCap) === null || _d === void 0 ? void 0 : _d.map((item) => formatDate(item.time))) || [],
                    label: 'ALPHA Market Cap',
                    TVL: ((_e = response.alphaHistoricalMarketCap) === null || _e === void 0 ? void 0 : _e.map((item) => parseFloat(item.TVL))) || [],
                }, treasuryHoldingsDistribution: response.treasuryHoldingsDistribution
                    ? [
                        {
                            stSUI: response.treasuryHoldingsDistribution.STSUI || '0',
                            ALPHA: response.treasuryHoldingsDistribution.ALPHA || '0',
                        },
                    ]
                    : [], airdropApr: ((_f = Number(response.airdropApr)) === null || _f === void 0 ? void 0 : _f.toFixed(2)) || '0', graphThemes: {
                    tooltipTextColor: themeName === 'dark' ? '#FFFFFF' : '#293036',
                    tooltipBgColor: themeName === 'dark' ? '#202226' : '#FFFFFF',
                    tooltipBorderColor: themeName === 'default' ? '#DEE4EF' : '#202226',
                } });
            setAnalyticsData(transformedData);
        }
        catch (error) {
            setError('Failed to fetch analytics data. ' + error);
        }
    });
    // Update graphThemes immediately when theme changes, without re-fetching data
    useEffect(() => {
        if (analyticsData && lastThemeName.current !== themeName) {
            lastThemeName.current = themeName;
            setAnalyticsData((prevData) => {
                if (!prevData)
                    return prevData;
                return Object.assign(Object.assign({}, prevData), { graphThemes: {
                        tooltipTextColor: themeName === 'dark' ? '#FFFFFF' : '#293036',
                        tooltipBgColor: themeName === 'dark' ? '#202226' : '#FFFFFF',
                        tooltipBorderColor: themeName === 'default' ? '#DEE4EF' : '#202226',
                    } });
            });
        }
    }, [themeName, analyticsData]);
    useEffect(() => {
        fetchAnalyticsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (error) {
        return _jsx("div", { children: error });
    }
    if (!analyticsData) {
        return (_jsx("div", { className: "flex items-center justify-center h-screen mt-[-4vw] theme-bg-alphafi-primary", children: _jsx(LoaderComp, { dimensions: "w-[9vw] md:w-[3vw] h-[9vw] md:h-[3vw]", borderWidth: "border-[0.5vw]" }) }));
    }
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "mt-[-1px]", children: [_jsx("div", { className: "text-xl md:text-2xl xl:text-3xl font-InterBold theme-bg-alphafi-primary theme-text-alphafi-primary py-[36px] xl:py-[64px] text-center", children: "Analytics" }), _jsx("div", { className: "mt-[-1px]", children: _jsx(AnalyticsV1, Object.assign({}, analyticsData, { graphThemes: analyticsData === null || analyticsData === void 0 ? void 0 : analyticsData.graphThemes })) })] }) }));
};
export default AnalyticsPage;
